import {
  ClaimAssignableCanon,
  JobAssignable,
  VendorRatesAssignable,
} from '@eigtech/assignments-types'
import { Contact as ContactType } from '@eigtech/contacts-types'
import { formatUsPhoneToHtml } from '@eigtech/contacts-util'
import { isJobId } from '@eigtech/restoration-types'
import { getJobLabelFromId } from '@eigtech/restoration-util'
import { getCornKernels } from '@eigtech/shared-corn'
import {
  getContactName,
  getContactRoleLabel,
  useGetAssignablesForContact,
} from '@eigtech/ui-shared-contacts'
import { DateTime, formatStringAsDate } from '@eigtech/ui-shared-dates'
import {
  Badge,
  Property as CProperty,
  ComposedCard,
  ComposedCardProps,
  ComposedModal,
  ComposedModalWrapperProps,
  DataSource,
  Divider,
  FancyLink,
  HStack,
  Heading,
  Icon,
  IconProps,
  List,
  ListItem,
  PropertyList,
  PropertyProps,
  Skeleton,
  Stack,
  StackProps,
  Tag,
  Text,
  Tooltip,
  Wrap,
  WrapItem,
  forwardRef,
  getGoogleMapQueryLink,
} from '@eigtech/ui-shared-dave'
import { useGetVendorRatesDetail } from '@eigtech/ui-shared-invoicing'
import { formatPhoneNumber } from '@eigtech/ui-shared-phone'
import { Link, useRoutesContext } from '@eigtech/ui-shared-router'
import { groupBy, startCase, uniqBy } from 'lodash-es'
import { FaMapMarkedAlt } from 'react-icons/fa'

export type ContactDetailProps = {
  contact: ContactType
} & ComposedCardProps

export const ContactDetail = forwardRef<ContactDetailProps, 'div'>(function Contact(
  { contact, ...props },
  ref
) {
  return (
    <ComposedCard ref={ref} {...props} heading={<ContactHead contact={contact} />}>
      <ContactBody contact={contact} />
    </ComposedCard>
  )
})

export function ViewContactModal({
  contact,
  ...modalProps
}: ComposedModalWrapperProps & { contact: ContactType }) {
  return (
    <ComposedModal {...modalProps} title={<ContactHead contact={contact} pr="8" />}>
      <ContactBody contact={contact} />
    </ComposedModal>
  )
}

export type ContactHeadProps = {
  contact: ContactType
  formatDate?: (dateTime: DateTime, format?: string | undefined) => string
} & StackProps

const ContactHead = forwardRef<ContactHeadProps, 'div'>(function ContactHead(
  { contact, formatDate = formatStringAsDate, ...props },
  ref
) {
  const { metadata } = contact

  return (
    <HStack ref={ref} justifyContent="space-between" {...props}>
      <Heading as="h3" size="sm">
        {getContactName(contact)}
      </Heading>

      <HStack>
        {!!metadata.removedOn && (
          <Tooltip label={formatDate(String(metadata.removedOn))} placement="top">
            <Badge colorScheme="red">Removed</Badge>
          </Tooltip>
        )}

        {!!metadata.dataSource && <DataSource>{metadata.dataSource}</DataSource>}
      </HStack>
    </HStack>
  )
})

const ContactBody = forwardRef<StackProps & Pick<ContactDetailProps, 'contact'>, 'div'>(
  function ContactBody({ contact, ...props }, ref) {
    const {
      addresses,
      communicationInformation,
      preferredMethodOfContact,
      roles,
      location,
      description,
    } = contact

    return (
      <Stack ref={ref} {...props} divider={<Divider />}>
        {!!addresses.length && (
          <PropertyList>
            {addresses.map((address, index) => (
              <Property
                key={address.rawAddress}
                data-key={address.addressType}
                label={
                  address.addressType
                    ? `${startCase(address.addressType)} Address`
                    : `Address ${index + 1}`
                }
                value={
                  <FancyLink
                    customIcon={MapIcon}
                    href={getGoogleMapQueryLink(address.rawAddress)}
                    isExternal
                    mb="0"
                    ml="2"
                  >
                    {address.rawAddress}
                  </FancyLink>
                }
              />
            ))}
          </PropertyList>
        )}

        {!!communicationInformation.length && (
          <PropertyList>
            <Property
              label="Preferred Contact Method"
              value={
                !!preferredMethodOfContact && preferredMethodOfContact !== 'unset'
                  ? preferredMethodOfContact
                  : 'Unknown'
              }
            />
            {communicationInformation.map((item, index) => {
              const isEmail = !!item.emailAddress

              return (
                <Property
                  key={index}
                  data-key={index}
                  label={
                    isEmail
                      ? 'Email'
                      : `Phone (${
                          (item.telecommunicationUseCode ?? item.preferenceOrder === 0)
                            ? 'Primary'
                            : 'Unknown Type'
                        })`
                  }
                  value={
                    item.emailAddress || item.unformattedTelephoneNumber ? (
                      <FancyLink
                        href={
                          isEmail
                            ? `mailto:${item.emailAddress}`
                            : `tel:${formatUsPhoneToHtml(item.unformattedTelephoneNumber ?? '')}`
                        }
                      >
                        {isEmail
                          ? item.emailAddress
                          : formatPhoneNumber(item.unformattedTelephoneNumber ?? '')}
                      </FancyLink>
                    ) : (
                      'Unknown'
                    )
                  }
                />
              )
            })}
          </PropertyList>
        )}

        <PropertyList>
          <Property
            label="Role(s)"
            value={
              <Wrap>
                {roles.map(getContactRoleLabel).map((role) => (
                  <WrapItem key={role}>
                    <Tag>{role}</Tag>
                  </WrapItem>
                ))}
              </Wrap>
            }
          />

          {!!location && (
            <Property
              label="Location"
              value={
                <FancyLink
                  href={getGoogleMapQueryLink({ lat: location.latitude, long: location.longitude })}
                  isExternal
                >
                  {location.latitude}, {location.longitude}
                </FancyLink>
              }
            />
          )}

          {!!description && <Property label="Description" value={description} />}
        </PropertyList>

        <PropertyList>
          <Property label="XactNet ID" value={contact.metadata.xactnetId ?? 'None'} />
        </PropertyList>

        <PropertyList>
          <Property label="Description" value={contact.description || 'None'} />
        </PropertyList>

        <Assignments contact={contact} />
      </Stack>
    )
  }
)

function Assignments({ contact }: Pick<ContactDetailProps, 'contact'>) {
  const { data, isPending } = useGetAssignablesForContact(contact.contactId)
  const assignments = uniqBy(data ?? [], 'assignable.assignableId')
  const { claim: claims, job: jobs, vendorRates } = groupBy(assignments, 'assignable.type')

  return (
    <Stack divider={<Divider />}>
      <PropertyList>
        <Property
          label="Claim Assignments"
          value={
            isPending ? (
              <Skeleton>Loading...</Skeleton>
            ) : claims?.length ? (
              <List>
                {claims.map(
                  (assignment) =>
                    assignment.assignable.type === 'claim' && (
                      <ListItem
                        key={`${assignment.assignable.assignableId}${assignment.assignable.assigneeRelationship}`}
                      >
                        <ClaimLink claimInfo={assignment.assignable} />
                      </ListItem>
                    )
                )}
              </List>
            ) : (
              <Text>Not assigned to any claims.</Text>
            )
          }
        />
      </PropertyList>

      <PropertyList>
        <Property
          label="Job Assignments"
          value={
            isPending ? (
              <Skeleton>Loading...</Skeleton>
            ) : jobs?.length ? (
              <List>
                {jobs.map(
                  (assignment) =>
                    assignment.assignable.type === 'job' && (
                      <ListItem
                        key={`${assignment.assignable.assignableId}${assignment.assignable.assigneeRelationship}`}
                      >
                        <JobLink jobInfo={assignment.assignable} />
                      </ListItem>
                    )
                )}
              </List>
            ) : (
              <Text>Not assigned to any jobs.</Text>
            )
          }
        />
      </PropertyList>

      <PropertyList>
        <Property
          label="Vendor Rates Assignments"
          value={
            isPending ? (
              <Skeleton>Loading...</Skeleton>
            ) : vendorRates?.length ? (
              <List>
                {vendorRates.map(
                  (assignment) =>
                    assignment.assignable.type === 'vendorRates' && (
                      <ListItem
                        key={`${assignment.assignable.assignableId}${assignment.assignable.assigneeRelationship}`}
                      >
                        <VendorRatesLink vendorRates={assignment.assignable} />
                      </ListItem>
                    )
                )}
              </List>
            ) : (
              <Text>Not assigned to any vendor rates.</Text>
            )
          }
        />
      </PropertyList>
    </Stack>
  )
}

function ClaimLink({ claimInfo }: { claimInfo: ClaimAssignableCanon }) {
  const { claimDetail } = useRoutesContext()

  const { resourceId: claimNumber } = getCornKernels(claimInfo.assignableId)

  return claimDetail ? (
    <Link params={{} as never} search={{} as never} to={claimDetail(claimNumber)}>
      {claimNumber}
    </Link>
  ) : (
    <Text>{claimNumber}</Text>
  )
}

function JobLink({ jobInfo }: { jobInfo: JobAssignable }) {
  const { jobDetail } = useRoutesContext()

  const { resourceId: jobId } = getCornKernels(jobInfo.assignableId)

  const jobLabel = isJobId(jobId) ? getJobLabelFromId(jobId) : jobId

  return !!jobDetail && isJobId(jobId) ? (
    <Link params={{} as never} search={{} as never} to={jobDetail(jobId)}>
      {jobLabel}
    </Link>
  ) : (
    <Text>{jobLabel}</Text>
  )
}

function VendorRatesLink({
  vendorRates: vendorRatesAssignable,
}: {
  vendorRates: VendorRatesAssignable
}) {
  const { vendorRatesDetail } = useRoutesContext()

  const { data: vendorRates, isPending } = useGetVendorRatesDetail({
    vendorRatesId: vendorRatesAssignable.assignableId,
  })

  const label = vendorRates?.name ?? 'Loading...'

  return (
    <Skeleton isLoaded={!isPending}>
      {vendorRatesDetail ? (
        <Link
          params={{} as never}
          search={{} as never}
          to={vendorRatesDetail({ vendorRatesId: vendorRatesAssignable.assignableId })}
        >
          {label}
        </Link>
      ) : (
        <Text>{label}</Text>
      )}
    </Skeleton>
  )
}

const Property = forwardRef<PropertyProps, 'dl'>(function Property(props, ref) {
  return <CProperty ref={ref} labelWidth="52" {...props} />
})

const MapIcon = forwardRef<IconProps, 'svg'>((props, ref) => (
  <Icon ref={ref} {...props} as={FaMapMarkedAlt} />
))
